import { Controller, useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { HomeIcon } from '@heroicons/react/24/outline';
import { GoogleAutoCompleteInput } from '@restworld/ui-ds-base';
import { Checkbox, Heading, Button } from '@restworld/ui-ds-base';
import { AddressFieldType, useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { useAddressAdditionalFields } from '@restworld/utils-common';

import { SaveResearchFormFields } from '../job-explorer-save-research-v2';

interface IAddressEditViewProps {
  callback?: () => void;
}

export const AddressEditView = ({ callback }: IAddressEditViewProps) => {
  const { control, getValues, setValue } = useFormContext<SaveResearchFormFields>();
  const workerService = useWorkersService();

  const { user } = useAuth();
  // const [modifyUserLocality, setModifyUserLocality] = useState(false)
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState<string | ''>('');
  const [addressFieldsTempState, setAddressFields] = useState<AddressFieldType | null>(null);

  const addressFields = useAddressAdditionalFields({ addressString: address, surface: 'worker_save_research_address' });

  const handleModifyAddress = useCallback(async () => {
    if (getValues('modify_user_locality')) {
      try {
        setValue('address', addressFieldsTempState);
        setValue('address_lon', addressFieldsTempState?.address_lon);
        setValue('address_lat', addressFieldsTempState?.address_lat);
        setAddressFields(null);
        setAddress('');
        setLoading(true);
        workerService.updateWorker({
          workerId: user?.id,
          params: {
            ...addressFieldsTempState,
          },
        });
        callback();
      } catch (error) {
        console.error(error, `Error updating worker address`);
      } finally {
        setValue('modify_user_locality', false);
        setLoading(false);
      }
    } else {
      setValue('address', addressFieldsTempState);
      setValue('address_lon', addressFieldsTempState?.address_lon);
      setValue('address_lat', addressFieldsTempState?.address_lat);
      setAddressFields(null);
      setAddress(null);
      callback();
    }
  }, [addressFieldsTempState, callback, getValues, setValue, user?.id, workerService]);

  useEffect(() => {
    if (addressFields && Object.keys(addressFields).length) setAddressFields(addressFields);
  }, [addressFields, setValue]);
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="flex flex-col gap-8 pb-6 max-h-[calc(100svh_-_16rem)] md:max-w-sm md:min-h-[calc(100svh_-_24rem)] overflow-y-auto px-2">
        <div className="flex flex-col gap-2">
          <Heading size={3} className="leading-[28.8px]">
            {t('job-explorer-page:change_search_area')}
          </Heading>
          <Heading size={5} weight="normal" className="leading-[24px]">
            {t('job-explorer-page:the_saved_search_area_address_matches_your_last_search')}
          </Heading>
        </div>
        <div className="flex gap-6 flex-col">
          <div className="flex flex-col gap-1">
            <Heading size={5}>{t('job-explorer-page:reference_location')}</Heading>
            <span className="flex flex-row items-center gap-1.5">
              <HomeIcon className="h-6 w-6 text-gray-600" />
              <Heading>{getValues('address.address')}</Heading>
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <Heading size={5} weight="normal" className="leading-[24px]">
              {t('job-explorer-page:new_reference_location')}
            </Heading>
            <GoogleAutoCompleteInput
              formId="research-address-input"
              trackingSurface="worker_save_research_address"
              key="research-address-input"
              placeholder={t('job-explorer-page:enter_address_or_location')}
              placesFilterTypes={['geocode']}
              formValue={address}
              onSuggestionSelect={({ description }) => {
                setAddress(description);
              }}
              control={control}
              autoFocus
              dropdownMainWrapperStyle={'max-w-menu'}
            />
            <Heading size={6} weight="normal" color="muted" className="leading-[21px]">
              {t('job-explorer-page:will_use_this_information_to_define_the_search_area')}
            </Heading>
          </div>
          <Controller
            name="modify_user_locality"
            control={control}
            render={({ field }) => (
              <div className="md:pt-20">
                <Checkbox
                  title={
                    <Heading size={6} weight="normal" className="leading-[21px]">
                      {t('job-explorer-page:use_the_new_address_in_your_profile')}
                    </Heading>
                  }
                  isChecked={Boolean(field.value)}
                  value={field.value}
                  disabled={!addressFieldsTempState}
                  {...field}
                />
              </div>
            )}
          />
        </div>
      </div>
      <div className="flex gap-2 flex-col w-full fixed bottom-2 px-6 left-0 md:relative md:px-0">
        <Button variant="primary" fullwidth onClick={handleModifyAddress} loading={loading}>
          {t('common:save_changes')}
        </Button>
        <Button
          variant="secondary-neutral"
          fullwidth
          onClick={() => {
            setValue('modify_user_locality', false);
            setAddressFields(null);
            setAddress(null);
            callback();
          }}
        >
          {t('common:cancel')}
        </Button>
      </div>
    </div>
  );
};

export default AddressEditView;
